import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';
import { getArticleById, getRelatedArticles, filterArticles, getAllSpecials } from '@/utils/data';
import { formatDateTime, formatViews } from '@/utils/format';
import ShareBar from './components/ShareBar';
import RelatedList from './components/RelatedList';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  useSeo({
    title: article ? `${article.title} - 51吃瓜网` : '资讯详情 - 51吃瓜网',
    description: article ? article.summary.slice(0, 120) : '51吃瓜网资讯详情页',
    keywords: article ? article.tags.join(',') : '51吃瓜网',
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-forbid-line text-3xl" />
          </div>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-900">该资讯已被删除或不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">换个链接试试，或者回到首页看看今天的热点。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedArticles(article, 4);
  const hotList = filterArticles({ sort: 'views' }).filter((a) => a.id !== article.id).slice(0, 5);
  const specials = [...getAllSpecials()].sort((a, b) => b.views - a.views).slice(0, 3);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: article.categoryName, to: `/category/${article.category}` }, { label: '正文' }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4">
              <i className="ri-price-tag-3-line" /> {article.subCategoryName}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">{article.title}</h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-user-line" /> {article.author}</span>
              <span className="flex items-center gap-1"><i className="ri-bookmark-line" /> 来源：{article.source}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line" /> {formatDateTime(article.publishTime)}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line" /> {formatViews(article.views)} 阅读</span>
            </div>

            <div className="mt-6 w-full rounded-lg overflow-hidden bg-background-200 aspect-[16/9]">
              <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top" />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((block, i) => {
                if (block.type === 'image') {
                  return (
                    <figure key={i} className="w-full">
                      <div className="w-full rounded-lg overflow-hidden bg-background-200 aspect-[16/9]">
                        <img src={block.src} alt={block.caption || article.title} className="w-full h-full object-cover object-top" />
                      </div>
                      {block.caption && <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>}
                    </figure>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote key={i} className="border-l-4 border-accent-500 bg-accent-50 rounded-r-md px-5 py-4 text-foreground-800 leading-relaxed">
                      {block.text}
                    </blockquote>
                  );
                }
                return (
                  <p key={i} className="text-[15px] md:text-base text-foreground-800 leading-loose">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-hashtag" /> {tag}
                </Link>
              ))}
            </div>

            <ShareBar />

            <div className="mt-8">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" /> 返回{article.categoryName}
              </Link>
            </div>

            <RelatedList articles={related} />
          </article>

          <aside className="lg:col-span-1 space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2 mb-3">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-fire-line text-sm" />
                </span>
                热门推荐
              </h2>
              <ol className="space-y-1">
                {hotList.map((item, i) => (
                  <li key={item.id}>
                    <Link to={`/detail/${item.id}`} className="group flex gap-3 py-3 border-b border-background-100 last:border-0">
                      <span className={`w-5 h-5 shrink-0 flex items-center justify-center rounded text-xs font-bold ${i < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'}`}>
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2 mb-3">
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-bookmark-3-line text-sm" />
                </span>
                热门专题
              </h2>
              <div className="space-y-2">
                {specials.map((sp) => (
                  <Link
                    key={sp.id}
                    to={`/special/${sp.id}`}
                    className="block px-3 py-2.5 rounded-md bg-background-100 hover:bg-primary-50 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                  >
                    {sp.title}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}